import { Trans } from '@lingui/react/macro';
import React, { useMemo, useState } from 'react';
import { Button, ButtonBar, NoItemsCard, Tabs } from '@ygtechnologies/rubus-ui';
import { faPlusCircle } from '@fortawesome/pro-light-svg-icons';
import { LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';

import { ProjectRelativeLink } from '@/components/navigation';
import { useCanScroll, useGetProjectId, useQueryState, useProjectRelativeRedirect } from '@/utils/hooks';
import { useFilterOptionGroups } from '@/services/optionGroups';

import theme from './OptionGroupsPage.module.scss';
import { getEmptyProjectOptionFilter, OptionsFilter, ProjectOptionFilter } from './OptionFilters/OptionsFilter';
import { OptionGroupAccordion } from './OptionGroupAccordion/OptionGroupAccordion';
import { useSearchOptionGroups } from '@/services/options/option-groups.hooks';
import { SortByDropdown, SortProps, SortBy } from './OptionFilters/SortByDropdown';
import { sortOptionGroups } from './OptionFilters/sortUtils';
import { OptionGroupSearchParams } from '@/services/options/option-groups';
import { OptionGroupLinkStatus } from '@/types';
import ScrollToWrapper from '@/components/ScrollToWrapper';
import { useLocation } from 'react-router';

export interface OptionGroupsPageProps {
    defaultPublishedStatusFilter?: OptionGroupLinkStatus[];
    publishedStatusFilterToggleValues?: OptionGroupLinkStatus[];
}

export const OptionGroupsPage: React.FC<OptionGroupsPageProps> = ({
    defaultPublishedStatusFilter,
    publishedStatusFilterToggleValues,
}) => {
    const projectId = useGetProjectId();
    const navigate = useProjectRelativeRedirect();
    const getDefaultFilter = useMemo(
        () => () => ({
            ...getEmptyProjectOptionFilter(),
            publishedStatus: defaultPublishedStatusFilter ?? [],
        }),
        [defaultPublishedStatusFilter]
    );
    const [filter, setFilter] = useQueryState<ProjectOptionFilter>(getDefaultFilter());
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [isExpandedAll, setIsExpandedAll] = useState(false);
    const [currentSort, setCurrentSort] = useState<SortProps>({ id: SortBy.Title, direction: 'asc' });
    const location = useLocation();
    const groupIdFromQuery = useMemo(() => {
        const groupId = new URLSearchParams(location.search).get('groupId');
        if (!groupId) {
            return undefined;
        }

        const parsed = Number(groupId);
        return Number.isNaN(parsed) ? undefined : parsed;
    }, [location.search]);
    const canScroll = useCanScroll(groupIdFromQuery);
    const { data: filterData } = useFilterOptionGroups(projectId);

    const searchParams = useMemo<OptionGroupSearchParams>(
        () => ({
            projectId: projectId!,
            searchText: filter.searchText || undefined,
            tagIds: filter.tagIds.length > 0 ? filter.tagIds : undefined,
            unitIds: filter.unitIds.length > 0 ? filter.unitIds : undefined,
            deadlineIds: filter.deadlines.length > 0 ? filter.deadlines : undefined,
            itemCategoryIds: filter.itemCategoryIds.length > 0 ? filter.itemCategoryIds : undefined,
            providedByProfileIds: filter.providedByProfileIds?.length ? filter.providedByProfileIds : undefined,
            roomIds: filter.roomIds?.length ? filter.roomIds : undefined,
            type: filter.linkType || undefined,
            publishedStatus: filter.publishedStatus,
        }),
        [projectId, filter]
    );
    const { data: optionGroups, isLoading } = useSearchOptionGroups(searchParams);

    const sortedOptionGroups = useMemo(() => {
        if (!optionGroups) {
            return null;
        }
        //TODO: Move sort to server
        return sortOptionGroups(optionGroups, currentSort);
    }, [optionGroups, currentSort]);

    function renderData() {
        if (isLoading || !sortedOptionGroups) {
            return <LoadingIndicator />;
        }

        if (sortedOptionGroups.length === 0) {
            return (
                <NoItemsCard
                    title={<Trans>No option groups found</Trans>}
                    description={<Trans>Want to create an option group?</Trans>}
                    buttonProps={{
                        icon: faPlusCircle,
                        onClick: () => navigate('/options/new'),
                        children: <Trans>New option group</Trans>,
                    }}
                />
            );
        }

        return sortedOptionGroups.map((og) => (
            <ScrollToWrapper key={og.id} shouldScroll={canScroll && og.id === groupIdFromQuery}>
                <OptionGroupAccordion optionGroup={og} isOpen={isExpandedAll || og.id === groupIdFromQuery} />
            </ScrollToWrapper>
        ));
    }

    const getToggleButtonText = (status: OptionGroupLinkStatus) => {
        if (status === OptionGroupLinkStatus.Pending) {
            return <Trans>Pending</Trans>;
        } else if (status === OptionGroupLinkStatus.Rejected) {
            return <Trans>Rejected</Trans>;
        }
        return <Trans>All</Trans>;
    };

    return (
        <div className={theme.container}>
            <ButtonBar sticky margins="none">
                {!publishedStatusFilterToggleValues && (
                    <>
                        <Button variant="tertiary" icon={faPlusCircle} as={ProjectRelativeLink} to="/options/new">
                            <Trans>New option group</Trans>
                        </Button>
                        <Button variant="tertiary" onClick={() => setIsExpandedAll(!isExpandedAll)}>
                            {isExpandedAll ? <Trans>Collapse all</Trans> : <Trans>Expand all</Trans>}
                        </Button>
                    </>
                )}
                {publishedStatusFilterToggleValues && (
                    <Tabs
                        activeId={
                            filter.publishedStatus?.length === 1 ? filter.publishedStatus[0] : ''
                        }
                        onChange={(id) => setFilter({ ...filter, publishedStatus: [id as OptionGroupLinkStatus] })}
                        variant="pill"
                        size="sm">
                        {publishedStatusFilterToggleValues.map((status) => (
                            <Tabs.Tab key={status} id={status} label={getToggleButtonText(status)} />
                        ))}
                    </Tabs>
                )}
                <ButtonBar.Right>
                    <SortByDropdown currentSort={currentSort} onSortChange={setCurrentSort} />
                    <OptionsFilter
                        filterData={filterData}
                        isLoading={isLoading}
                        filter={filter}
                        onChange={setFilter}
                        isFilterOpen={isFilterOpen}
                        onFilterOpenChange={setIsFilterOpen}
                        getEmptyFilter={defaultPublishedStatusFilter ? getDefaultFilter : undefined}
                    />
                </ButtonBar.Right>
            </ButtonBar>

            {renderData()}
        </div>
    );
};
